import { ICloneable } from '../../contracts/cloneable.js';
import { IDataComparable } from '../../contracts/data-comparable.js';
import { compareTo, ComparisonResult } from '../../utility/comparison.js';

/**
 * Represents a cardinal number.
 * Cardinal numbers are used to measure the number of elements in a set.
 * Ranges from 0 to infinity. Finite values are integers.
 *
 * See also: https://en.wikipedia.org/wiki/Cardinal_number
 */
export class Cardinal implements IDataComparable, ICloneable {
  /**
     * Corresponds to Aleph-null.
     */
  static readonly _INFINITY: number = -1;

  public readonly value: number;

  private constructor(
      value: number,
  ) {
    if(Number.isSafeInteger(value)) {
      if(value >= 0) {
        this.value = value;
      }
      else {
        this.value = Cardinal._INFINITY;
      }
    }
    else {
      this.value = 0;
    }
  }

  static readonly infinity = new Cardinal(Cardinal._INFINITY);

  static finite(value: number): Cardinal {
    return new Cardinal(value);
  }

  get isInfinite(): boolean {
    return this.value === Cardinal._INFINITY;
  }

  get isFinite(): boolean {
    return !this.isInfinite;
  }

  encode(): number {
    return this.value;
  }

  static decode(value: number): Cardinal {
    return new Cardinal(value);
  }

  clone(): this {
    if(this.isInfinite) {
      return Cardinal.infinity as this;
    }

    return new Cardinal(this.value) as this;
  }

  isSame(other: Cardinal): boolean {
    if(other === this) {
      return true;
    }

    return other.value === this.value;
  }

  compareTo(other: number | Cardinal): ComparisonResult {
    if(typeof other === 'number') {
      return this.compareToNumber(other);
    }
    else {
      return this.compareToCardinal(other);
    }
  }

  private compareToNumber(other: number): ComparisonResult {
    if(this.isInfinite) {
      return 1;
    }

    const abs = Cardinal.finite(Math.abs(other));
    return this.compareTo(abs);
  }

  private compareToCardinal(other: Cardinal): ComparisonResult {
    if(this.isInfinite) {
      if(other.isInfinite) {
        return 0;
      }

      return 1;
    }
    else {
      if(other.isInfinite) {
        return -1;
      }

      return compareTo(this.value, other.value);
    }
  }
}
